import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useFetchSearchOffersDesktopService } from '@/packages/query-state/shared/store/search-offers/v3/search-offers-desktop/hooks/useFetchSearchOffersDesktopService';
import { getQueryKey as getAddToComparisonQueryKey } from '@/packages/query-state/shared/store/сomparison/v1/add-to-comparison/internal/config';
import { getQueryKey as getDeleteFromComparisonQueryKey } from '@/packages/query-state/shared/store/сomparison/v1/delete-from-comparison/internal/config';

import { type TSearchOffersDesktopResponse_1 } from '@/repositories/generated/shared/search-offers/v3/search-offers-desktop';

import { type TCardApiContext } from '../../types/TCardApiContext';
import { type TCardContextData } from '../../types/TCardContextData';

import { useRefetchClusters } from './internal/hooks/useRefetchClusters';
import { type InitCardApiContext } from './types';

export function useInitCardApi(): InitCardApiContext {
  const fetchGetOffersLoadService = useFetchSearchOffersDesktopService();

  const refetchClustersOnSuccess = useRefetchClusters();

  const queryClient = useQueryClient();

  return useCallback<(cardContextData: TCardContextData) => TCardApiContext>(
    cardContextData => ({
      hideOfferMutationOptions: {
        onMutate(): InfiniteData<TSearchOffersDesktopResponse_1> | null {
          const prev = fetchGetOffersLoadService.getQueryData();

          fetchGetOffersLoadService.removeOfferById(cardContextData.offer.id);

          return prev;
        },
        onError(_error, _variables, context: InfiniteData<TSearchOffersDesktopResponse_1> | null): void {
          if (context) {
            fetchGetOffersLoadService.setQueryData(context);
          }
        },
        onSuccess: refetchClustersOnSuccess,
      },
      addToFavoriteMutationOptions: {
        onError(_error, _variables, context: InfiniteData<TSearchOffersDesktopResponse_1> | null): void {
          if (context) {
            fetchGetOffersLoadService.setQueryData(context);
          }
        },
        onMutate(): InfiniteData<TSearchOffersDesktopResponse_1> | null {
          const prev = fetchGetOffersLoadService.getQueryData();

          fetchGetOffersLoadService.updateFavoriteByOfferId(cardContextData.offer.id, true);

          return prev;
        },
        onSuccess: refetchClustersOnSuccess,
      },
      deleteFromFavoriteMutationOptions: {
        onError(_error, _variables, context): void {
          if (context) {
            fetchGetOffersLoadService.setQueryData(context);
          }
        },
        onMutate(): InfiniteData<TSearchOffersDesktopResponse_1> | null {
          const prev = fetchGetOffersLoadService.getQueryData();

          fetchGetOffersLoadService.updateFavoriteByOfferId(cardContextData.offer.id, false);

          return prev;
        },
        onSuccess: refetchClustersOnSuccess,
      },
      addToComparisonMutationOptions: {
        onError(_error, _variables, context: InfiniteData<TSearchOffersDesktopResponse_1> | null): void {
          if (context) {
            fetchGetOffersLoadService.setQueryData(context);
          }
        },
        onMutate(): InfiniteData<TSearchOffersDesktopResponse_1> | null {
          const prev = fetchGetOffersLoadService.getQueryData();

          fetchGetOffersLoadService.updateCompareByOfferId(cardContextData.offer.id, {
            description: 'Добавлено в сравнение',
            status: 'added',
          });

          return prev;
        },
        onSuccess(data, _variables, prev): void {
          /**
           * @description
           * После запроса добавляем данные в кэш для нотификации
           */
          queryClient.setQueryData(getAddToComparisonQueryKey(), data);

          if (data.success) {
            refetchClustersOnSuccess();
          } else {
            fetchGetOffersLoadService.setQueryData(prev);
          }
        },
      },
      deleteFromComparisonMutationOptions: {
        onError(_error, _variables, context): void {
          if (context) {
            fetchGetOffersLoadService.setQueryData(context);
          }
        },
        onMutate(): InfiniteData<TSearchOffersDesktopResponse_1> | null {
          const prev = fetchGetOffersLoadService.getQueryData();

          fetchGetOffersLoadService.updateCompareByOfferId(cardContextData.offer.id, {
            description: 'Добавлено в сравнение',
            status: 'available',
          });

          return prev;
        },
        onSuccess(data): void {
          refetchClustersOnSuccess();

          /**
           * @description
           * После запроса добавляем данные в кэш для нотификации
           */
          queryClient.setQueryData(getDeleteFromComparisonQueryKey(), data);
        },
      },
    }),
    [fetchGetOffersLoadService, queryClient, refetchClustersOnSuccess],
  );
}
